import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, Plus,
  ArrowLeft, Star, CheckCircle, ChevronRight, Loader2, BarChart3,
  ShieldAlert, Edit, Send, XCircle, Play, FileText
} from 'lucide-react';
import { useAuth } from '../../../src/hooks/useAuth';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { useProjectBudgets, useProjectBudgetSummary, useProjectChangeOrders } from '../../../src/hooks/queries/use-budgets';
import {
  financialService,
  ProjectBudget, BudgetChangeOrder,
  BudgetType, BudgetStatus, ChangeOrderType, CostImpactType, ChangeOrderWorkflowStatus,
  ProjectBudgetCreateData, BudgetChangeOrderCreateData,
} from '../../../src/services/financial.service';
import { queryKeys } from '../../../src/lib/query-keys';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { formatCurrency, formatCurrencyCompact } from '../../../src/utils/currency';

// ─── Label maps ──────────────────────────────────────────────────────────────

const BUDGET_TYPE_LABELS: Record<BudgetType, string> = {
  original: 'Original',
  revised: 'Revised',
  forecast: 'Forecast',
  baseline: 'Baseline Type',
};

const BUDGET_STATUS_COLORS: Record<BudgetStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  locked: 'bg-amber-100 text-amber-700',
  closed: 'bg-purple-100 text-purple-700',
  archived: 'bg-gray-100 text-gray-500',
};

const CO_STATUS_COLORS: Record<ChangeOrderWorkflowStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  executed: 'bg-blue-100 text-blue-700',
};

const CO_STATUS_LABELS: Record<ChangeOrderWorkflowStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  executed: 'Executed',
};

const CHANGE_TYPES: { value: ChangeOrderType; label: string }[] = [
  { value: 'scope_addition', label: 'Scope Addition' },
  { value: 'scope_reduction', label: 'Scope Reduction' },
  { value: 'field_condition', label: 'Field Condition' },
  { value: 'owner_request', label: 'Owner Request' },
  { value: 'design_change', label: 'Design Change' },
  { value: 'force_majeure', label: 'Force Majeure' },
  { value: 'other', label: 'Other' },
];

const IMPACT_TYPES: { value: CostImpactType; label: string; color: string }[] = [
  { value: 'cost_increase', label: 'Cost Increase', color: 'text-red-600' },
  { value: 'cost_decrease', label: 'Cost Decrease', color: 'text-green-600' },
  { value: 'no_cost_impact', label: 'No Cost Impact', color: 'text-gray-600' },
];

const APPROVAL_LEVELS = [
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'senior_manager', label: 'Senior Manager' },
  { value: 'director', label: 'Director' },
  { value: 'board', label: 'Board' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Coerce API value (may arrive as string) to number. */
const num = (v: any): number => Number(v ?? 0);

function utilizationColor(pct: number) {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 85) return 'bg-amber-400';
  return 'bg-emerald-500';
}

function coImpactColor(impact: CostImpactType | null) {
  if (impact === 'cost_increase') return 'text-red-600';
  if (impact === 'cost_decrease') return 'text-green-600';
  return 'text-gray-600';
}

function MetricCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className={`text-xl font-bold mt-0.5 ${accent ?? ''}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function CostBar({ label, amount, total, currency }: { label: string; amount: number | null; total: number; currency: string }) {
  if (!amount || amount === 0) return null;
  const pct = total > 0 ? Math.min((amount / total) * 100, 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{formatCurrencyCompact(amount, currency)}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary/60" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Create Budget Dialog ─────────────────────────────────────────────────────

interface CreateBudgetDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  projectId: string;
  existingCount: number;
  orgCurrency: string;
  onSaved: () => void;
}

function CreateBudgetDialog({ open, onOpenChange, orgId, projectId, existingCount, orgCurrency, onSaved }: CreateBudgetDialogProps) {
  const [saving, setSaving] = useState(false);
  const year = new Date().getFullYear();
  const suggestedNumber = `BUD-${year}-${String(existingCount + 1).padStart(3, '0')}`;

  const [form, setForm] = useState({
    budget_name: '',
    budget_number: suggestedNumber,
    budget_type: 'original' as BudgetType,
    version: '1.0',
    budget_date: new Date().toISOString().split('T')[0],
    original_amount: '',
    labor_budget: '',
    material_budget: '',
    equipment_budget: '',
    subcontractor_budget: '',
    other_direct_costs: '',
    contingency_amount: '',
    currency_code: orgCurrency,
    description: '',
  });

  useEffect(() => {
    if (open) {
      setForm(f => ({ ...f, budget_number: `BUD-${year}-${String(existingCount + 1).padStart(3, '0')}` }));
    }
  }, [open, existingCount, year]);

  const breakdownSum = [form.labor_budget, form.material_budget, form.equipment_budget, form.subcontractor_budget, form.other_direct_costs]
    .reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const handleSave = async () => {
    if (!form.budget_name.trim()) { showError({ title: 'Validation', message: 'Budget name is required' }); return; }
    if (!form.budget_number.trim()) { showError({ title: 'Validation', message: 'Budget number is required' }); return; }
    if (!form.original_amount || parseFloat(form.original_amount) <= 0) { showError({ title: 'Validation', message: 'Original amount must be greater than 0' }); return; }
    setSaving(true);
    try {
      const amount = parseFloat(form.original_amount);
      const payload: ProjectBudgetCreateData = {
        organization_id: orgId,
        project_id: projectId,
        budget_name: form.budget_name.trim(),
        budget_number: form.budget_number.trim(),
        budget_type: form.budget_type,
        version: form.version || '1.0',
        budget_date: form.budget_date,
        original_amount: amount,
        current_budget: amount,
        labor_budget: parseFloat(form.labor_budget) || undefined,
        material_budget: parseFloat(form.material_budget) || undefined,
        equipment_budget: parseFloat(form.equipment_budget) || undefined,
        subcontractor_budget: parseFloat(form.subcontractor_budget) || undefined,
        other_direct_costs: parseFloat(form.other_direct_costs) || undefined,
        contingency_amount: parseFloat(form.contingency_amount) || undefined,
        currency_code: form.currency_code || orgCurrency,
        description: form.description || undefined,
      };
      await financialService.createProjectBudget(payload);
      showSuccess({ title: 'Budget Created', message: form.budget_name });
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Failed to Create Budget', message: err?.message ?? 'An error occurred' });
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />New Budget
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Identity */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Budget Identity</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 grid gap-1.5">
                <Label>Budget Name *</Label>
                <Input value={form.budget_name} onChange={field('budget_name')} placeholder="e.g., South Cape Mall — Original Budget" />
              </div>
              <div className="grid gap-1.5">
                <Label>Budget Number *</Label>
                <Input value={form.budget_number} onChange={field('budget_number')} />
              </div>
              <div className="grid gap-1.5">
                <Label>Version</Label>
                <Input value={form.version} onChange={field('version')} placeholder="1.0" />
              </div>
              <div className="grid gap-1.5">
                <Label>Budget Type *</Label>
                <Select value={form.budget_type} onValueChange={v => setForm(p => ({ ...p, budget_type: v as BudgetType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original">Original</SelectItem>
                    <SelectItem value="revised">Revised</SelectItem>
                    <SelectItem value="forecast">Forecast</SelectItem>
                    <SelectItem value="baseline">Baseline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Budget Date *</Label>
                <Input type="date" value={form.budget_date} onChange={field('budget_date')} />
              </div>
            </div>
          </div>

          {/* Amounts */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Budget Amounts</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 grid gap-1.5">
                <Label>Total Original Amount *</Label>
                <Input type="number" min="0" value={form.original_amount} onChange={field('original_amount')} placeholder="0.00" />
              </div>
              <div className="grid gap-1.5">
                <Label>Currency</Label>
                <Input value={form.currency_code} onChange={field('currency_code')} placeholder="USD" maxLength={3} />
              </div>
              <div className="grid gap-1.5">
                <Label>Contingency Amount</Label>
                <Input type="number" min="0" value={form.contingency_amount} onChange={field('contingency_amount')} placeholder="0.00" />
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cost Breakdown</p>
              {breakdownSum > 0 && (
                <span className="text-xs text-muted-foreground">
                  Sum: {formatCurrencyCompact(breakdownSum, form.currency_code || orgCurrency)}
                  {form.original_amount && Math.abs(breakdownSum - parseFloat(form.original_amount)) > 0.01 && (
                    <span className="text-amber-600 ml-1">
                      ({breakdownSum > parseFloat(form.original_amount) ? '+' : ''}{formatCurrencyCompact(breakdownSum - parseFloat(form.original_amount), form.currency_code || orgCurrency)})
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'labor_budget' as const, label: 'Labor' },
                { key: 'material_budget' as const, label: 'Materials' },
                { key: 'equipment_budget' as const, label: 'Equipment' },
                { key: 'subcontractor_budget' as const, label: 'Subcontractors' },
                { key: 'other_direct_costs' as const, label: 'Other Direct Costs' },
              ].map(({ key, label }) => (
                <div key={key} className="grid gap-1.5">
                  <Label>{label}</Label>
                  <Input type="number" min="0" value={form[key]} onChange={field(key)} placeholder="0.00" />
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="grid gap-1.5">
            <Label>Description / Notes</Label>
            <Textarea value={form.description} onChange={field('description')} rows={2} placeholder="Optional notes…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create Budget
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Create Change Order Dialog ───────────────────────────────────────────────

interface CreateCODialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  projectId: string;
  budgetId: string;
  existingCount: number;
  orgCurrency: string;
  onSaved: () => void;
}

function CreateChangeOrderDialog({ open, onOpenChange, orgId, projectId, budgetId, existingCount, orgCurrency, onSaved }: CreateCODialogProps) {
  const [saving, setSaving] = useState(false);
  const year = new Date().getFullYear();

  const [form, setForm] = useState({
    change_order_number: `CO-${year}-${String(existingCount + 1).padStart(3, '0')}`,
    title: '',
    description: '',
    change_type: '' as ChangeOrderType | '',
    impact_type: 'cost_increase' as CostImpactType,
    cost_impact: '',
    labor_cost_change: '',
    material_cost_change: '',
    equipment_cost_change: '',
    subcontractor_cost_change: '',
    schedule_impact_days: '',
    reason_for_change: '',
    request_date: new Date().toISOString().split('T')[0],
    required_decision_date: '',
    approval_level_required: '' as string,
  });

  useEffect(() => {
    if (open) {
      setForm(f => ({
        ...f,
        change_order_number: `CO-${year}-${String(existingCount + 1).padStart(3, '0')}`,
      }));
    }
  }, [open, existingCount, year]);

  const field = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim()) { showError({ title: 'Validation', message: 'Title is required' }); return; }
    if (!form.cost_impact || parseFloat(form.cost_impact) === 0) { showError({ title: 'Validation', message: 'Cost impact is required' }); return; }
    setSaving(true);
    try {
      const payload: BudgetChangeOrderCreateData = {
        organization_id: orgId,
        project_id: projectId,
        budget_id: budgetId,
        change_order_number: form.change_order_number.trim(),
        title: form.title.trim(),
        description: form.description || undefined,
        change_type: form.change_type || undefined,
        impact_type: form.impact_type,
        cost_impact: parseFloat(form.cost_impact),
        labor_cost_change: parseFloat(form.labor_cost_change) || undefined,
        material_cost_change: parseFloat(form.material_cost_change) || undefined,
        equipment_cost_change: parseFloat(form.equipment_cost_change) || undefined,
        subcontractor_cost_change: parseFloat(form.subcontractor_cost_change) || undefined,
        schedule_impact_days: parseInt(form.schedule_impact_days) || undefined,
        reason_for_change: form.reason_for_change || undefined,
        request_date: form.request_date,
        required_decision_date: form.required_decision_date || undefined,
        approval_level_required: (form.approval_level_required as any) || undefined,
      };
      await financialService.createBudgetChangeOrder(payload);
      showSuccess({ title: 'Change Order Created', message: form.title });
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Failed to Create Change Order', message: err?.message ?? 'An error occurred' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />New Change Order
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Identity */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Change Order Identity</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>CO Number</Label>
                <Input value={form.change_order_number} onChange={field('change_order_number')} />
              </div>
              <div className="grid gap-1.5">
                <Label>Change Type</Label>
                <Select value={form.change_type} onValueChange={v => setForm(p => ({ ...p, change_type: v as ChangeOrderType }))}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {CHANGE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 grid gap-1.5">
                <Label>Title *</Label>
                <Input value={form.title} onChange={field('title')} placeholder="e.g., Foundation depth increase" />
              </div>
              <div className="col-span-2 grid gap-1.5">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={field('description')} rows={2} placeholder="Brief description of the change…" />
              </div>
            </div>
          </div>

          {/* Cost Impact */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cost Impact</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Impact Type *</Label>
                <Select value={form.impact_type} onValueChange={v => setForm(p => ({ ...p, impact_type: v as CostImpactType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {IMPACT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Total Cost Impact ({orgCurrency}) *</Label>
                <Input type="number" min="0" value={form.cost_impact} onChange={field('cost_impact')} placeholder="0.00" />
              </div>
              {[
                { key: 'labor_cost_change' as const, label: 'Labor Change' },
                { key: 'material_cost_change' as const, label: 'Materials Change' },
                { key: 'equipment_cost_change' as const, label: 'Equipment Change' },
                { key: 'subcontractor_cost_change' as const, label: 'Subcontractor Change' },
              ].map(({ key, label }) => (
                <div key={key} className="grid gap-1.5">
                  <Label className="text-muted-foreground">{label}</Label>
                  <Input type="number" value={form[key]} onChange={field(key)} placeholder="0.00" />
                </div>
              ))}
              <div className="grid gap-1.5">
                <Label>Schedule Impact (days)</Label>
                <Input type="number" value={form.schedule_impact_days} onChange={field('schedule_impact_days')} placeholder="0" />
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Details & Approval</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 grid gap-1.5">
                <Label>Reason for Change</Label>
                <Textarea value={form.reason_for_change} onChange={field('reason_for_change')} rows={2} placeholder="Why is this change needed?" />
              </div>
              <div className="grid gap-1.5">
                <Label>Request Date</Label>
                <Input type="date" value={form.request_date} onChange={field('request_date')} />
              </div>
              <div className="grid gap-1.5">
                <Label>Decision Required By</Label>
                <Input type="date" value={form.required_decision_date} onChange={field('required_decision_date')} />
              </div>
              <div className="col-span-2 grid gap-1.5">
                <Label>Approval Level Required</Label>
                <Select value={form.approval_level_required} onValueChange={v => setForm(p => ({ ...p, approval_level_required: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                  <SelectContent>
                    {APPROVAL_LEVELS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create Change Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Change Order Row Actions ─────────────────────────────────────────────────

interface COActionsProps {
  co: BudgetChangeOrder;
  onRefresh: () => void;
}

function COActions({ co, onRefresh }: COActionsProps) {
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const run = async (action: () => Promise<any>, successMsg: string) => {
    setBusy(true);
    try {
      await action();
      showSuccess({ title: successMsg });
      onRefresh();
    } catch (err: any) {
      showError({ title: 'Action Failed', message: err?.message });
    } finally {
      setBusy(false);
    }
  };

  if (co.status === 'draft') {
    return (
      <Button size="sm" variant="outline" disabled={busy} className="gap-1.5"
        onClick={() => run(() => financialService.submitBudgetChangeOrder(co.change_order_id), 'Submitted for Approval')}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        Submit
      </Button>
    );
  }
  if (co.status === 'pending_approval') {
    return (
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" disabled={busy} className="gap-1.5 text-green-700 border-green-200 hover:bg-green-50"
          onClick={() => run(() => financialService.approveBudgetChangeOrder(co.change_order_id), 'Change Order Approved')}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
          Approve
        </Button>
        <Button size="sm" variant="outline" disabled={busy} className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50"
          onClick={() => setRejectOpen(true)}>
          <XCircle className="h-3.5 w-3.5" />Reject
        </Button>
        <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reject Change Order?</AlertDialogTitle>
              <AlertDialogDescription>
                <Textarea
                  className="mt-2"
                  placeholder="Rejection reason…"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  rows={3}
                />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground"
                onClick={() => run(() => financialService.rejectBudgetChangeOrder(co.change_order_id, rejectReason), 'Change Order Rejected')}>
                Reject
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }
  if (co.status === 'approved') {
    return (
      <Button size="sm" variant="outline" disabled={busy} className="gap-1.5 text-blue-700 border-blue-200 hover:bg-blue-50"
        onClick={() => run(() => financialService.executeBudgetChangeOrder(co.change_order_id), 'Change Order Executed')}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        Execute
      </Button>
    );
  }
  return null;
}

// ─── Budget Detail View ───────────────────────────────────────────────────────

interface BudgetDetailViewProps {
  budget: ProjectBudget;
  orgId: string;
  projectId: string;
  orgCurrency: string;
  onBack: () => void;
  onBudgetRefresh: () => void;
}

function BudgetDetailView({ budget, orgId, projectId, orgCurrency, onBack, onBudgetRefresh }: BudgetDetailViewProps) {
  const currency = budget.currency_code || orgCurrency;
  const [approvingBudget, setApprovingBudget] = useState(false);
  const [settingBaseline, setSettingBaseline] = useState(false);
  const [coOpen, setCoOpen] = useState(false);

  const { data: changeOrders = [], refetch: refetchCOs } = useProjectChangeOrders(orgId, projectId);
  const budgetCOs = (changeOrders as BudgetChangeOrder[]).filter(co => co.budget_id === budget.budget_id);

  const handleApproveBudget = async () => {
    setApprovingBudget(true);
    try {
      await financialService.approveProjectBudget(budget.budget_id);
      showSuccess({ title: 'Budget Approved' });
      onBudgetRefresh();
    } catch (err: any) {
      showError({ title: 'Failed to Approve', message: err?.message });
    } finally {
      setApprovingBudget(false);
    }
  };

  const handleSetBaseline = async () => {
    setSettingBaseline(true);
    try {
      await financialService.setBaselineProjectBudget(budget.budget_id);
      showSuccess({ title: 'Baseline Set', message: budget.budget_name });
      onBudgetRefresh();
    } catch (err: any) {
      showError({ title: 'Failed to Set Baseline', message: err?.message });
    } finally {
      setSettingBaseline(false);
    }
  };

  const util = num(budget.utilization_percentage);
  const variance = budget.variance_amount ?? 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0 mt-0.5">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold truncate">{budget.budget_name}</h3>
            <span className="text-sm text-muted-foreground">{budget.budget_number}</span>
            <Badge variant="secondary">{BUDGET_TYPE_LABELS[budget.budget_type]}</Badge>
            <Badge className={BUDGET_STATUS_COLORS[budget.status]}>{budget.status}</Badge>
            {budget.is_baseline && (
              <Badge className="bg-amber-100 text-amber-700 gap-1"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />Baseline</Badge>
            )}
            {budget.is_over_budget && (
              <Badge className="bg-red-100 text-red-700 gap-1"><ShieldAlert className="h-3 w-3" />Over Budget</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            v{budget.version} · {budget.budget_date}
            {budget.approved_by && ` · Approved ${budget.approval_date ?? ''}`}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {budget.status === 'draft' && (
            <Button size="sm" variant="outline" onClick={handleApproveBudget} disabled={approvingBudget} className="gap-1.5">
              {approvingBudget ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
              Approve
            </Button>
          )}
          {budget.status === 'approved' && !budget.is_baseline && (
            <Button size="sm" variant="outline" onClick={handleSetBaseline} disabled={settingBaseline} className="gap-1.5">
              {settingBaseline ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />}
              Set Baseline
            </Button>
          )}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Original Budget" value={formatCurrencyCompact(budget.original_amount, currency)} />
        <MetricCard
          label="Current Budget"
          value={formatCurrencyCompact(budget.current_budget, currency)}
          sub={budget.approved_changes ? `+${formatCurrencyCompact(budget.approved_changes, currency)} approved changes` : undefined}
        />
        <MetricCard label="Actual Costs" value={formatCurrencyCompact(budget.actual_amount, currency)}
          sub={budget.committed_amount ? `+${formatCurrencyCompact(budget.committed_amount, currency)} committed` : undefined} />
        <MetricCard
          label={budget.is_over_budget ? 'Over Budget' : 'Remaining'}
          value={formatCurrencyCompact(Math.abs(budget.remaining_budget), currency)}
          accent={budget.is_over_budget ? 'text-red-600' : 'text-emerald-600'}
        />
      </div>

      {/* Utilization + Variance */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Budget Utilization</span>
            <span className={`font-bold ${util >= 100 ? 'text-red-600' : util >= 85 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {util.toFixed(1)}%
            </span>
          </div>
          <div className="h-3 rounded-full bg-secondary overflow-hidden">
            <div className={`h-full rounded-full transition-all ${utilizationColor(util)}`} style={{ width: `${Math.min(util, 100)}%` }} />
          </div>
          <div className="flex items-center justify-between text-sm pt-1">
            <div className="flex items-center gap-4 text-muted-foreground">
              {budget.pending_changes > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <TrendingUp className="h-3.5 w-3.5" />Pending: {formatCurrencyCompact(budget.pending_changes, currency)}
                </span>
              )}
              {budget.approved_changes > 0 && (
                <span>Approved changes: {formatCurrencyCompact(budget.approved_changes, currency)}</span>
              )}
            </div>
            <div className={`flex items-center gap-1 font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {variance >= 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
              {variance >= 0 ? 'Under' : 'Over'} by {formatCurrencyCompact(Math.abs(variance), currency)}
              {budget.variance_percentage != null && ` (${Math.abs(num(budget.variance_percentage)).toFixed(1)}%)`}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Breakdown */}
      {(budget.labor_budget || budget.material_budget || budget.equipment_budget || budget.subcontractor_budget || budget.other_direct_costs || budget.contingency_amount) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" />Cost Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <CostBar label="Labor" amount={budget.labor_budget} total={budget.original_amount} currency={currency} />
            <CostBar label="Materials" amount={budget.material_budget} total={budget.original_amount} currency={currency} />
            <CostBar label="Equipment" amount={budget.equipment_budget} total={budget.original_amount} currency={currency} />
            <CostBar label="Subcontractors" amount={budget.subcontractor_budget} total={budget.original_amount} currency={currency} />
            <CostBar label="Other Direct Costs" amount={budget.other_direct_costs} total={budget.original_amount} currency={currency} />
            <CostBar label="Contingency" amount={budget.contingency_amount} total={budget.original_amount} currency={currency} />
            {budget.overhead_amount && <CostBar label="Overhead" amount={budget.overhead_amount} total={budget.original_amount} currency={currency} />}
            {budget.risk_reserve_amount && <CostBar label="Risk Reserve" amount={budget.risk_reserve_amount} total={budget.original_amount} currency={currency} />}
          </CardContent>
        </Card>
      )}

      {/* Change Orders */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Change Orders
            {budget.pending_change_order_count > 0 && (
              <Badge className="bg-amber-100 text-amber-700">{budget.pending_change_order_count} pending</Badge>
            )}
          </h4>
          <Button size="sm" className="gap-1.5" onClick={() => setCoOpen(true)}>
            <Plus className="h-4 w-4" />Add Change Order
          </Button>
        </div>

        {budgetCOs.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No change orders yet for this budget.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Impact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {budgetCOs.map(co => (
                  <TableRow key={co.change_order_id}>
                    <TableCell className="font-mono text-sm">{co.change_order_number}</TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{co.title}</p>
                      {co.reason_for_change && <p className="text-xs text-muted-foreground truncate max-w-[180px]">{co.reason_for_change}</p>}
                    </TableCell>
                    <TableCell>
                      {co.change_type && <Badge variant="outline" className="text-xs">{CHANGE_TYPES.find(t => t.value === co.change_type)?.label ?? co.change_type}</Badge>}
                    </TableCell>
                    <TableCell className={`font-medium text-sm ${coImpactColor(co.impact_type)}`}>
                      {co.impact_type === 'cost_increase' ? '+' : co.impact_type === 'cost_decrease' ? '−' : ''}
                      {formatCurrencyCompact(Math.abs(co.cost_impact), currency)}
                      {co.schedule_impact_days && co.schedule_impact_days !== 0 && (
                        <span className="block text-xs text-muted-foreground">{co.schedule_impact_days > 0 ? '+' : ''}{co.schedule_impact_days}d</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={CO_STATUS_COLORS[co.status]}>{CO_STATUS_LABELS[co.status]}</Badge>
                    </TableCell>
                    <TableCell>
                      <COActions co={co} onRefresh={refetchCOs} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <CreateChangeOrderDialog
        open={coOpen}
        onOpenChange={setCoOpen}
        orgId={orgId}
        projectId={projectId}
        budgetId={budget.budget_id}
        existingCount={budgetCOs.length}
        orgCurrency={currency}
        onSaved={refetchCOs}
      />
    </div>
  );
}

// ─── Budget List Item ─────────────────────────────────────────────────────────

function BudgetListItem({ budget, orgCurrency, onClick }: { budget: ProjectBudget; orgCurrency: string; onClick: () => void }) {
  const currency = budget.currency_code || orgCurrency;
  const util = num(budget.utilization_percentage);
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{budget.budget_name}</span>
          <span className="text-xs text-muted-foreground">{budget.budget_number}</span>
          <Badge variant="secondary" className="text-xs">{BUDGET_TYPE_LABELS[budget.budget_type]}</Badge>
          <Badge className={`text-xs ${BUDGET_STATUS_COLORS[budget.status]}`}>{budget.status}</Badge>
          {budget.is_baseline && <Badge className="text-xs bg-amber-100 text-amber-700 gap-1"><Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />Baseline</Badge>}
          {budget.is_over_budget && <Badge className="text-xs bg-red-100 text-red-700">Over Budget</Badge>}
        </div>
        <div className="flex items-center gap-4 mt-1.5">
          <div className="flex-1 max-w-[200px]">
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className={`h-full rounded-full ${utilizationColor(util)}`} style={{ width: `${Math.min(util, 100)}%` }} />
            </div>
          </div>
          <span className="text-xs text-muted-foreground">{util.toFixed(1)}% utilized</span>
          <span className="text-xs text-muted-foreground">
            {formatCurrencyCompact(budget.current_budget, currency)} budget
          </span>
          {budget.pending_change_order_count > 0 && (
            <span className="text-xs text-amber-600">{budget.pending_change_order_count} pending CO{budget.pending_change_order_count > 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </div>
  );
}

// ─── Main Budget Tab ──────────────────────────────────────────────────────────

export function BudgetTab({ projectId }: { projectId: string }) {
  const { orgId } = useAuth();
  const orgCurrency = useOrgCurrency();
  const qc = useQueryClient();

  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: summaryRaw, isError: summaryError } = useProjectBudgetSummary(projectId);
  const { data: budgets = [], refetch: refetchBudgets } = useProjectBudgets(orgId, projectId);

  const summary = summaryRaw as any;
  const budgetList = budgets as ProjectBudget[];
  const selectedBudget = budgetList.find(b => b.budget_id === selectedBudgetId);

  const invalidateAll = () => {
    refetchBudgets();
    qc.invalidateQueries({ queryKey: queryKeys.budgets.projectSummary(projectId) });
  };

  if (selectedBudget) {
    return (
      <BudgetDetailView
        budget={selectedBudget}
        orgId={orgId ?? ''}
        projectId={projectId}
        orgCurrency={orgCurrency}
        onBack={() => setSelectedBudgetId(null)}
        onBudgetRefresh={invalidateAll}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Project-wide summary */}
      {summary && !summaryError && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            label="Total Current Budget"
            value={formatCurrencyCompact(summary.total_current_budget ?? 0, orgCurrency)}
            sub={`${summary.active_budgets_count ?? 0} active budget${(summary.active_budgets_count ?? 0) !== 1 ? 's' : ''}`}
          />
          <MetricCard
            label="Actual Costs"
            value={formatCurrencyCompact(summary.total_actual_costs ?? 0, orgCurrency)}
          />
          <MetricCard
            label="Committed"
            value={formatCurrencyCompact(summary.total_committed_costs ?? 0, orgCurrency)}
            sub={summary.pending_change_orders_count > 0 ? `${summary.pending_change_orders_count} COs pending` : undefined}
          />
          <MetricCard
            label={summary.budgets_over_limit > 0 ? '⚠ Remaining' : 'Remaining'}
            value={formatCurrencyCompact(summary.total_remaining_budget ?? 0, orgCurrency)}
            accent={summary.budgets_over_limit > 0 ? 'text-red-600' : 'text-emerald-600'}
            sub={summary.variance_percentage != null ? `${num(summary.variance_percentage) > 0 ? '+' : ''}${num(summary.variance_percentage).toFixed(1)}% variance` : undefined}
          />
        </div>
      )}

      {/* Budget list header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Project Budgets</h3>
        <Button size="sm" className="gap-1.5" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />Add Budget
        </Button>
      </div>

      {/* Budget list */}
      {budgetList.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center">
            <DollarSign className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="font-medium text-muted-foreground">No budgets set up</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Create a budget to start tracking project costs.</p>
            <Button onClick={() => setCreateOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />Create First Budget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {budgetList.map(b => (
            <BudgetListItem
              key={b.budget_id}
              budget={b}
              orgCurrency={orgCurrency}
              onClick={() => setSelectedBudgetId(b.budget_id)}
            />
          ))}
        </div>
      )}

      <CreateBudgetDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={orgId ?? ''}
        projectId={projectId}
        existingCount={budgetList.length}
        orgCurrency={orgCurrency}
        onSaved={invalidateAll}
      />
    </div>
  );
}
